import React, { useState } from "react";
import { Phone, MessageCircle, ChevronRight, Star } from "lucide-react";
import { CONTACT } from "../data/mock";

export function HeroSection({ onGetQuote }) {
  const [form, setForm] = useState({ name: "", phone: "", city: "Hyderabad" });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    const msg = `Hello! I'm ${form.name} from ${form.city}. I'm interested in your interior design services. My number is ${form.phone}.`;
    window.open(
      `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(msg)}`,
      "_blank"
    );
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)",
      }}
    >
      {/* Background image overlay */}
      <div
        className="absolute inset-0 opacity-25"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/20418771/pexels-photo-20418771.jpeg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* Gold accent lines */}
      <div className="absolute top-0 left-0 w-full h-1 bg-[#C9A84C]" />
      <div className="absolute bottom-0 left-0 w-full h-1 bg-[#C9A84C]" />

      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-64 h-64 rounded-full border border-[#C9A84C]/20 hidden lg:block" />
      <div className="absolute top-32 right-20 w-40 h-40 rounded-full border border-[#C9A84C]/10 hidden lg:block" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-2 gap-12 items-center w-full">
        {/* Left content */}
        <div className="text-white" data-testid="hero-content">
          <div className="inline-flex items-center gap-2 bg-[#C9A84C]/20 border border-[#C9A84C]/40 rounded-full px-4 py-1.5 mb-6">
            <Star size={12} className="text-[#C9A84C] fill-[#C9A84C]" />
            <span className="text-[#C9A84C] text-xs font-semibold tracking-wider uppercase">Hyderabad's #1 Interior Designers</span>
          </div>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            Transform Your Home Into a
            <span className="block text-[#C9A84C] italic">Dream Space</span>
          </h1>

          <p className="text-gray-300 text-base sm:text-lg mb-8 leading-relaxed max-w-lg">
            Hyderabad's most trusted interior designers. From modular kitchens to complete home interiors, 
            we bring your vision to life with stunning 3D designs and flawless execution.
          </p>

          {/* Stats row */}
          <div className="flex flex-wrap gap-6 mb-8">
            {[
              { num: "500+", label: "Happy Homes" },
              { num: "10+", label: "Years Experience" },
              { num: "100%", label: "On-time" },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-2xl font-bold text-[#C9A84C]">{s.num}</div>
                <div className="text-xs text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={onGetQuote}
              className="flex items-center gap-2 bg-[#C9A84C] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#b8933e] transition-colors"
              data-testid="hero-get-quote-btn"
            >
              Get Free Quote
              <ChevronRight size={16} />
            </button>
            <a
              href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(CONTACT.whatsappMsg)}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 bg-[#25D366] text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#1fb856] transition-colors"
              data-testid="hero-whatsapp-btn"
            >
              <MessageCircle size={16} />
              WhatsApp Us
            </a>
          </div>
        </div>

        {/* Right: Lead form */}
        <div className="bg-white rounded-2xl shadow-2xl p-7 max-w-md w-full mx-auto" data-testid="hero-form">
          <h2 className="font-serif text-2xl font-bold text-[#1a1a1a] mb-1">Get Free Consultation</h2>
          <p className="text-gray-500 text-sm mb-6">Our Hyderabad designers will call you back within 30 minutes</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
              <input
                type="text"
                placeholder="Enter your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#C9A84C] transition-colors"
                required
                data-testid="hero-form-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="tel"
                placeholder="Your mobile number"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#C9A84C] transition-colors"
                required
                data-testid="hero-form-phone"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Area in Hyderabad</label>
              <select
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#C9A84C] bg-white"
                data-testid="hero-form-area"
              >
                {["Gachibowli", "HITEC City", "Kondapur", "Madhapur", "Banjara Hills", "Jubilee Hills", "Kukatpally", "Kompally", "Miyapur", "LB Nagar", "Secunderabad", "Ameerpet", "Other"].map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="w-full bg-[#C9A84C] text-white font-bold py-3.5 rounded-lg hover:bg-[#b8933e] transition-colors"
              data-testid="hero-form-submit"
            >
              Book Free Consultation
            </button>
          </form>

          <div className="flex items-center justify-center gap-4 mt-5 pt-4 border-t border-gray-100">
            <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-2 text-sm text-[#1a1a1a] font-medium hover:text-[#C9A84C] transition-colors">
              <Phone size={14} />
              {CONTACT.phone}
            </a>
            <span className="text-gray-300">|</span>
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(s => (
                <Star key={s} size={12} className="fill-[#C9A84C] text-[#C9A84C]" />
              ))}
              <span className="text-xs text-gray-500 ml-1">5.0</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
